import React, { useEffect, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import styled from 'styled-components';
import { motion, domAnimation, m } from 'framer-motion';
import { FiX, FiLogOut } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import LogoutModal from './LogoutModal';

const ScannerContainer = styled(motion.div)`
  width: 100%;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  flex-direction: column;
  z-index: 1000;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const ScannerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px;
`;

const ScannerView = styled.div`
  width: 300px;
  height: 300px;
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    background: #ff0000;
    animation: scan 2s linear infinite;
  }

  @keyframes scan {
    0% {
      top: 0;
    }
    100% {
      top: 100%;
    }
  }
`;

const CloseButton = styled(motion.button)`
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 10px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  span {
    font-size: 16px;
    font-weight: 500;
    color: #333;
  }
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Logo = styled.div`
  width: 45px;
  height: 45px;
  background-color: #ff0000;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 10px;
  text-align: center;
  line-height: 1.2;
`;

const ItemCodeSticker = styled.div`
  width: 300px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
`;

const StickerLabel = styled.div`
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;

const StickerCode = styled.div`
  font-size: 18px;
  color: #333;
  font-weight: 600;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #333;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  
  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`;

interface QRScannerProps {
  onClose: () => void;
  onScan: (result: string) => void;
}

const QRScanner: React.FC<QRScannerProps> = ({ onClose, onScan }) => {
  const [scanner, setScanner] = useState<Html5Qrcode | null>(null);
  const [permissionError, setPermissionError] = useState<string>('');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [cameraStarted, setCameraStarted] = useState(false);
  const navigate = useNavigate();

  const handleClose = useCallback(async () => {
    if (scanner?.isScanning) {
      try {
        await scanner.stop();
      } catch (error) {
        console.error('Error stopping scanner:', error);
      }
    }
    onClose();
  }, [scanner, onClose]);

  useEffect(() => {
    const html5QrCode = new Html5Qrcode("reader");
    setScanner(html5QrCode);

    return () => {
      if (html5QrCode.isScanning) {
        html5QrCode.stop().catch((error: Error) => {
          console.error('Error stopping scanner during cleanup:', error);
        });
      }
      try {
        html5QrCode.clear();
      } catch (error) {
        console.error('Error clearing scanner:', error);
      }
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const startScanning = async () => {
      if (scanner && !scanner.isScanning && !cameraStarted && isMounted) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment" }
          });
          
          if (!isMounted) {
            stream.getTracks().forEach(track => track.stop());
            return;
          }

          stream.getTracks().forEach(track => track.stop());
          
          if (!isMounted) return;

          await scanner.start(
            { facingMode: "environment" },
            {
              fps: 10,
              qrbox: { width: 250, height: 250 },
              aspectRatio: 1
            },
            async (decodedText: string) => {
              if (isMounted) {
                onScan(decodedText);
                await handleClose();
              }
            },
            (errorMessage: string) => {
              console.warn('QR Scanner warning:', errorMessage);
            }
          );
          setCameraStarted(true);
        } catch (err) {
          if (!isMounted) return;
          
          console.error('Camera error:', err);
          if (err instanceof Error) {
            if (err.name === 'NotAllowedError') {
              setPermissionError('Camera access was denied. Please allow camera access and try again.');
            } else if (err.name === 'NotFoundError') {
              setPermissionError('No camera found. Please ensure your device has a camera.');
            } else if (err.name === 'NotReadableError') {
              setPermissionError('Camera is in use by another application. Please close other apps using the camera.');
            } else {
              setPermissionError('Failed to start camera. Please check camera permissions and try again.');
            }
          }
        }
      }
    };

    startScanning();

    return () => {
      isMounted = false;
      if (scanner?.isScanning) {
        scanner.stop().catch(console.error);
      }
    };
  }, [scanner, handleClose, onScan, cameraStarted]);

  const renderContent = () => {
    if (permissionError) {
      return (
        <ScannerContainer
          key="error"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Header>
            <HeaderLeft>
              <ActionButton onClick={onClose}>
                <FiX size={24} />
              </ActionButton>
              <span>Camera Error</span>
            </HeaderLeft>
          </Header>
          <ScannerContent>
            <div style={{ 
              color: 'white', 
              textAlign: 'center', 
              padding: '20px',
              background: 'rgba(255, 0, 0, 0.1)',
              borderRadius: '10px',
              maxWidth: '300px'
            }}>
              <div style={{ marginBottom: '15px', color: '#ff0000' }}>⚠️</div>
              {permissionError}
            </div>
            <ActionButton 
              onClick={() => {
                setPermissionError('');
                setCameraStarted(false);
              }}
              style={{
                background: '#ff0000',
                color: 'white',
                padding: '10px 20px',
                borderRadius: '8px',
                marginTop: '10px'
              }}
            >
              Try Again
            </ActionButton>
          </ScannerContent>
        </ScannerContainer>
      );
    }

    return (
      <ScannerContainer
        key="scanner"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <Header>
          <HeaderLeft>
            <ActionButton onClick={handleClose}>
              <FiX size={24} />
            </ActionButton>
            <span>Scan QR Code</span>
          </HeaderLeft>
          <HeaderRight>
            <Logo>
              STYLE<br /><span style={{fontWeight: '900'}}>BOX</span>
            </Logo>
            <ActionButton onClick={() => setShowLogoutConfirm(true)}>
              <FiLogOut size={16} />
            </ActionButton>
          </HeaderRight>
        </Header>
        
        <ScannerContent>
          <ScannerView id="reader" />
          <ItemCodeSticker>
            <StickerLabel>SB Item Code</StickerLabel>
            <StickerCode>PCS/SUR/TIL/1178</StickerCode>
          </ItemCodeSticker>
        </ScannerContent>
      </ScannerContainer>
    );
  };

  return (
    <>
      {renderContent()}
      {showLogoutConfirm && <LogoutModal onClose={() => setShowLogoutConfirm(false)} />}
    </>
  );
};

export default QRScanner; 